import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  User, 
  Mail, 
  Calendar,
  Crown,
  Sparkles,
  LogOut
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-2xl mx-auto" data-testid="profile-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 mb-6 text-white text-3xl font-bold">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <h1 className="font-serif text-3xl font-bold text-foreground mb-2">
            {user?.name}
          </h1>
          {user?.is_premium && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              Crown Premium Member
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="space-y-4 mb-10">
          <div className="crown-card p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-semibold text-foreground">{user?.name}</p>
            </div>
          </div>

          <div className="crown-card p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-semibold text-foreground">{user?.email}</p>
            </div>
          </div>

          <div className="crown-card p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Member Since</p>
              <p className="font-semibold text-foreground">{formatDate(user?.created_at)}</p>
            </div>
          </div>

          <div className="crown-card p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Crown className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Membership</p>
              <p className="font-semibold text-foreground">
                {user?.is_premium ? 'Premium' : 'Free Tier'}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          {!user?.is_premium && (
            <Button
              onClick={() => navigate('/subscription')}
              className="w-full btn-gold rounded-full py-6 text-lg"
              data-testid="upgrade-btn"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Upgrade to Premium
            </Button>
          )}
          
          <Button
            onClick={handleLogout}
            variant="outline"
            data-testid="logout-btn"
            className="w-full rounded-full py-6 text-lg border-2 border-destructive text-destructive hover:bg-destructive/5"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Affirmation */}
        <div className="mt-10 glass-gold rounded-2xl p-6 text-center">
          <Crown className="w-8 h-8 text-amber-600 mx-auto mb-3" />
          <p className="font-scripture text-lg italic text-foreground">
            "You are doing brave, hard work. Be proud of yourself, Queen."
          </p>
        </div>
      </div>
    </Layout>
  );
}
